import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { getTutorialVideoUrl } from '../../services/TutorialService';

/**
 * Lightweight preview of how a tutorial appears in the player (title + video).
 * Not wired to progress/navigation — full player rewrite comes later.
 */
const TutorialPreviewDialog = ({ open, tutorial, orderIndex, onClose }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !tutorial?.storagePath) {
      setVideoUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = await getTutorialVideoUrl(tutorial.storagePath);
        if (!cancelled) setVideoUrl(url);
      }
      catch {
        if (!cancelled) setError('No se pudo cargar el vídeo');
      }
      finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, tutorial?.storagePath]);

  const positionLabel = orderIndex != null ? `${orderIndex + 1}. ` : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Vista previa
        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Así se verá aproximadamente en la sección de tutoriales (sin progreso ni navegación).
        </Typography>
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom>
            {`${positionLabel}${tutorial?.title ?? ''}`}
          </Typography>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
              {error}
            </Typography>
          )}
          {videoUrl && !loading && (
            <video
              controls
              controlsList="nodownload"
              disablePictureInPicture
              preload="metadata"
              width="100%"
              src={videoUrl}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialPreviewDialog;
